// src/components/workspace/costing/CLINBuilder.tsx
// AI-powered CLIN (Contract Line Item Number) Builder

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FileText, Plus, Trash2, ArrowLeft, Save, Loader2, Sparkles,
  DollarSign, Clock, Users, Package, ChevronDown, ChevronRight,
  AlertCircle, CheckCircle, Copy, Calculator, Zap, Info
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { formatCurrency } from '../../../lib/pricing/utils';
import toast from 'react-hot-toast';

// Types
type CLINType = 'FFP' | 'T&M' | 'CPFF' | 'CPIF' | 'IDIQ';

interface LaborMix {
  categoryId: string;
  categoryTitle: string;
  hours: number;
  baseRate: number;
  loadedRate: number;
}

interface CLIN {
  id: string;
  clinNumber: string;
  title: string;
  description: string;
  type: CLINType;
  period: 'base' | 'option1' | 'option2' | 'option3' | 'option4';
  laborMix: LaborMix[];
  odcs: Array<{ name: string; amount: number }>;
  totalLabor: number;
  totalODC: number;
  total: number;
  isExpanded: boolean;
}

interface LaborCategory {
  id: string;
  title: string;
  baseRate: number;
  loadedRate: number;
}

interface CLINBuilderProps {
  proposalId?: string;
}

const CLIN_TYPES: { value: CLINType; label: string; description: string }[] = [
  { value: 'FFP', label: 'Firm Fixed Price', description: 'Fixed price regardless of actual costs' },
  { value: 'T&M', label: 'Time & Materials', description: 'Labor rates plus materials at cost' },
  { value: 'CPFF', label: 'Cost Plus Fixed Fee', description: 'Reimbursed costs plus fixed fee' },
  { value: 'CPIF', label: 'Cost Plus Incentive Fee', description: 'Costs plus incentive-based fee' },
  { value: 'IDIQ', label: 'Indefinite Delivery', description: 'Task orders under ceiling price' }
];

const PERIODS = [
  { value: 'base', label: 'Base Period' },
  { value: 'option1', label: 'Option Year 1' },
  { value: 'option2', label: 'Option Year 2' },
  { value: 'option3', label: 'Option Year 3' },
  { value: 'option4', label: 'Option Year 4' }
];

export function CLINBuilder({ proposalId: propProposalId }: CLINBuilderProps) {
  const navigate = useNavigate();
  const { id: paramId } = useParams<{ id?: string }>();
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState(false);

  // Use proposalId from props, URL params, or let user select
  const [selectedProposalId, setSelectedProposalId] = useState<string | null>(propProposalId || paramId || null);
  const [proposals, setProposals] = useState<Array<{ id: string; title: string; agency: string }>>([]);
  const [proposalContentJson, setProposalContentJson] = useState<any>(null);
  const proposalId = selectedProposalId;

  const [clins, setClins] = useState<CLIN[]>([]);
  const [laborCategories, setLaborCategories] = useState<LaborCategory[]>([]);
  const [wrapRate, setWrapRate] = useState(2.15);
  const [contractTitle, setContractTitle] = useState('');

  useEffect(() => {
    if (profile) {
      if (proposalId) {
        loadData();
      } else {
        loadProposalList();
      }
    }
  }, [profile, proposalId]);

  // Load proposal list for selection when no proposalId is provided
  const loadProposalList = async () => {
    try {
      setLoading(true);
      const { data: proposalData } = await supabase
        .from('proposal_templates')
        .select('id, template_name, notice_id, content_json')
        .eq('profile_id', profile?.id)
        .order('created_at', { ascending: false });

      if (!proposalData || proposalData.length === 0) {
        setProposals([]);
        setLoading(false);
        return;
      }

      // Get contract details separately
      const noticeIds = [...new Set(proposalData.map(p => p.notice_id).filter(Boolean))];
      let contractsMap: Record<string, any> = {};
      if (noticeIds.length > 0) {
        const { data: contractsData } = await supabase
          .from('sam_contracts')
          .select('notice_id, title, agency')
          .in('notice_id', noticeIds);
        if (contractsData) {
          contractsMap = contractsData.reduce((acc: Record<string, any>, c: any) => {
            acc[c.notice_id] = c;
            return acc;
          }, {});
        }
      }

      setProposals(proposalData.map(p => ({
        id: p.id,
        title: contractsMap[p.notice_id]?.title || p.template_name || 'Untitled Proposal',
        agency: contractsMap[p.notice_id]?.agency || 'Unknown Agency'
      })));
    } catch (error) {
      console.error('Error loading proposals:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);

      // Load indirect rates first to calculate wrap rate
      let indirectData: any = null;
      try {
        const { data } = await supabase
          .from('company_indirect_rates')
          .select('*')
          .eq('profile_id', profile?.id)
          .maybeSingle();
        indirectData = data;
      } catch (e) {
        console.warn('company_indirect_rates not available:', e);
      }

      let currentWrapRate = 2.0; // Default wrap rate
      if (indirectData) {
        currentWrapRate = (1 + (indirectData.fringe_rate || 32) / 100) *
                    (1 + (indirectData.overhead_rate || 45) / 100) *
                    (1 + (indirectData.ga_rate || 12) / 100) *
                    (1 + (indirectData.profit_rate || 10) / 100);
        setWrapRate(currentWrapRate);
      }

      // Load company labor categories (from new table first, fallback to old structure)
      let companyCategories: LaborCategory[] = [];

      let companyLaborData: any[] | null = null;
      try {
        const { data } = await supabase
          .from('company_labor_categories')
          .select('*')
          .eq('profile_id', profile?.id)
          .eq('is_active', true)
          .order('sort_order');
        companyLaborData = data;
      } catch (e) {
        console.warn('company_labor_categories not available:', e);
      }

      if (companyLaborData && companyLaborData.length > 0) {
        companyCategories = companyLaborData.map((cat: any) => ({
          id: cat.id,
          title: cat.title,
          baseRate: cat.base_hourly_rate,
          loadedRate: cat.base_hourly_rate * currentWrapRate
        }));
      } else {
        // Fallback to old pricing_components structure
        const { data: pricingData } = await supabase
          .from('enhanced_pricing_data')
          .select('pricing_components(*)')
          .eq('profile_id', profile?.id);

        pricingData?.forEach(entry => {
          entry.pricing_components?.forEach((comp: any) => {
            if (comp.category === 'Labor') {
              companyCategories.push({
                id: comp.id,
                title: comp.name,
                baseRate: comp.unit_cost,
                loadedRate: comp.unit_cost * currentWrapRate
              });
            }
          });
        });
      }

      // Load proposal-specific data if we have a proposalId
      if (proposalId) {
        // Load from proposal_templates (the actual proposal data)
        const { data: proposalTemplate } = await supabase
          .from('proposal_templates')
          .select('id, template_name, content_json, notice_id')
          .eq('id', proposalId)
          .maybeSingle();

        // Get contract details separately
        let contractInfo: any = null;
        if (proposalTemplate?.notice_id) {
          const { data: contractData } = await supabase
            .from('sam_contracts')
            .select('title, agency')
            .eq('notice_id', proposalTemplate.notice_id)
            .maybeSingle();
          contractInfo = contractData;
        }

        if (proposalTemplate) {
          setContractTitle(contractInfo?.title || proposalTemplate.template_name || '');
          setProposalContentJson(proposalTemplate.content_json);

          // Extract labor categories from proposal's AI-generated content
          const proposalContent = proposalTemplate.content_json;

          // Check for labor categories in pricingStrategy
          if (proposalContent?.pricingStrategy?.laborCategories) {
            const proposalLabor = proposalContent.pricingStrategy.laborCategories;
            proposalLabor.forEach((laborCat: any) => {
              const existingIdx = companyCategories.findIndex(
                c => c.title.toLowerCase() === (laborCat.category || laborCat.title || '').toLowerCase()
              );

              if (existingIdx === -1) {
                // Add new category from proposal
                const baseRate = parseFloat(laborCat.hourlyRate?.replace(/[^0-9.]/g, '') || '0') ||
                                 parseFloat(laborCat.baseRate || '0') || 50;
                companyCategories.push({
                  id: `proposal-${Date.now()}-${Math.random()}`,
                  title: laborCat.category || laborCat.title || 'Unknown',
                  baseRate: baseRate,
                  loadedRate: baseRate * currentWrapRate
                });
              }
            });
          }

          // Check for labor in deliverables/staffing sections
          if (proposalContent?.staffingPlan?.laborCategories) {
            proposalContent.staffingPlan.laborCategories.forEach((laborCat: any) => {
              const existingIdx = companyCategories.findIndex(
                c => c.title.toLowerCase() === (laborCat.title || laborCat.name || '').toLowerCase()
              );

              if (existingIdx === -1) {
                const baseRate = laborCat.rate || laborCat.hourlyRate || 50;
                companyCategories.push({
                  id: `staffing-${Date.now()}-${Math.random()}`,
                  title: laborCat.title || laborCat.name || 'Unknown',
                  baseRate: typeof baseRate === 'string' ? parseFloat(baseRate.replace(/[^0-9.]/g, '')) || 50 : baseRate,
                  loadedRate: (typeof baseRate === 'string' ? parseFloat(baseRate.replace(/[^0-9.]/g, '')) || 50 : baseRate) * currentWrapRate
                });
              }
            });
          }

          // Load existing CLINs from proposal if available
          if (proposalContent?.pricingStrategy?.clinStructure) {
            setClins(proposalContent.pricingStrategy.clinStructure.map((c: any) => ({
              id: c.id || `clin-${Date.now()}-${Math.random()}`,
              clinNumber: c.clinNumber || '0001',
              title: c.title || c.description || '',
              description: c.description || '',
              type: c.contractType || c.type || 'FFP',
              period: c.periodOfPerformance || c.period || 'base',
              laborMix: c.laborMix || [],
              odcs: c.odcs || [],
              totalLabor: c.totalLabor || 0,
              totalODC: c.totalODC || 0,
              total: parseFloat((c.totalPrice || c.total || '0').toString().replace(/[^0-9.]/g, '')) || 0,
              isExpanded: false
            })));
          }
        }
      }

      setLaborCategories(companyCategories);

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addCLIN = () => {
    const nextNumber = clins.length + 1;
    setClins(prev => [...prev, {
      id: `clin-${Date.now()}`,
      clinNumber: String(nextNumber).padStart(4, '0'),
      title: '',
      description: '',
      type: 'FFP',
      period: 'base',
      laborMix: [],
      odcs: [],
      totalLabor: 0,
      totalODC: 0,
      total: 0,
      isExpanded: true
    }]);
  };

  const updateCLIN = (id: string, updates: Partial<CLIN>) => {
    setClins(prev => prev.map(clin => {
      if (clin.id !== id) return clin;

      const updated = { ...clin, ...updates };

      // Recalculate totals
      updated.totalLabor = updated.laborMix.reduce((sum, l) => sum + (l.hours * l.loadedRate), 0);
      updated.totalODC = updated.odcs.reduce((sum, o) => sum + o.amount, 0);
      updated.total = updated.totalLabor + updated.totalODC;

      return updated;
    }));
  };

  const removeCLIN = (id: string) => {
    setClins(prev => prev.filter(c => c.id !== id));
  };

  const duplicateCLIN = (clin: CLIN) => {
    const newClin: CLIN = {
      ...clin,
      id: `clin-${Date.now()}`,
      clinNumber: String(clins.length + 1).padStart(4, '0'),
      title: `${clin.title} (Copy)`,
      isExpanded: true
    };
    setClins(prev => [...prev, newClin]);
  };

  const addLaborToCLIN = (clinId: string, categoryId: string) => {
    const category = laborCategories.find(c => c.id === categoryId);
    if (!category) return;

    setClins(prev => prev.map(clin => {
      if (clin.id !== clinId) return clin;

      // Check if already added
      if (clin.laborMix.some(l => l.categoryId === categoryId)) return clin;

      const updated = {
        ...clin,
        laborMix: [...clin.laborMix, {
          categoryId,
          categoryTitle: category.title,
          hours: 0,
          baseRate: category.baseRate,
          loadedRate: category.loadedRate
        }]
      };

      return updated;
    }));
  };

  const updateLaborMix = (clinId: string, categoryId: string, hours: number) => {
    setClins(prev => prev.map(clin => {
      if (clin.id !== clinId) return clin;

      const updated = {
        ...clin,
        laborMix: clin.laborMix.map(l =>
          l.categoryId === categoryId ? { ...l, hours } : l
        )
      };

      // Recalculate totals
      updated.totalLabor = updated.laborMix.reduce((sum, l) => sum + (l.hours * l.loadedRate), 0);
      updated.total = updated.totalLabor + updated.totalODC;

      return updated;
    }));
  };

  const removeLaborFromCLIN = (clinId: string, categoryId: string) => {
    setClins(prev => prev.map(clin => {
      if (clin.id !== clinId) return clin;

      const updated = {
        ...clin,
        laborMix: clin.laborMix.filter(l => l.categoryId !== categoryId)
      };

      updated.totalLabor = updated.laborMix.reduce((sum, l) => sum + (l.hours * l.loadedRate), 0);
      updated.total = updated.totalLabor + updated.totalODC;

      return updated;
    }));
  };

  const addODCToCLIN = (clinId: string) => {
    setClins(prev => prev.map(clin => {
      if (clin.id !== clinId) return clin;

      return {
        ...clin,
        odcs: [...clin.odcs, { name: '', amount: 0 }]
      };
    }));
  };

  const updateODC = (clinId: string, index: number, updates: { name?: string; amount?: number }) => {
    setClins(prev => prev.map(clin => {
      if (clin.id !== clinId) return clin;

      const updated = {
        ...clin,
        odcs: clin.odcs.map((o, i) => i === index ? { ...o, ...updates } : o)
      };

      updated.totalODC = updated.odcs.reduce((sum, o) => sum + o.amount, 0);
      updated.total = updated.totalLabor + updated.totalODC;

      return updated;
    }));
  };

  const removeODC = (clinId: string, index: number) => {
    setClins(prev => prev.map(clin => {
      if (clin.id !== clinId) return clin;

      const updated = {
        ...clin,
        odcs: clin.odcs.filter((_, i) => i !== index)
      };

      updated.totalODC = updated.odcs.reduce((sum, o) => sum + o.amount, 0);
      updated.total = updated.totalLabor + updated.totalODC;

      return updated;
    }));
  };

  const suggestCLINStructure = async () => {
    setAiSuggesting(true);

    // Simulate AI suggestion (would call actual AI endpoint)
    setTimeout(() => {
      const suggestedClins: CLIN[] = [
        {
          id: `clin-${Date.now()}-1`,
          clinNumber: '0001',
          title: 'Program Management',
          description: 'Program management and oversight services',
          type: 'FFP',
          period: 'base',
          laborMix: laborCategories.slice(0, 2).map(cat => ({
            categoryId: cat.id,
            categoryTitle: cat.title,
            hours: 500,
            baseRate: cat.baseRate,
            loadedRate: cat.loadedRate
          })),
          odcs: [],
          totalLabor: 0,
          totalODC: 0,
          total: 0,
          isExpanded: true
        },
        {
          id: `clin-${Date.now()}-2`,
          clinNumber: '0002',
          title: 'Technical Services',
          description: 'Technical development and implementation',
          type: 'T&M',
          period: 'base',
          laborMix: laborCategories.slice(2, 5).map(cat => ({
            categoryId: cat.id,
            categoryTitle: cat.title,
            hours: 1000,
            baseRate: cat.baseRate,
            loadedRate: cat.loadedRate
          })),
          odcs: [{ name: 'Travel', amount: 25000 }],
          totalLabor: 0,
          totalODC: 25000,
          total: 0,
          isExpanded: false
        }
      ];

      // Calculate totals
      suggestedClins.forEach(clin => {
        clin.totalLabor = clin.laborMix.reduce((sum, l) => sum + (l.hours * l.loadedRate), 0);
        clin.total = clin.totalLabor + clin.totalODC;
      });

      setClins(suggestedClins);
      setAiSuggesting(false);
      toast.success('AI suggested CLIN structure added');
    }, 1500);
  };

  const handleSave = async () => {
    if (!proposalId) {
      toast.error('Please select a proposal to save CLINs');
      return;
    }

    try {
      setSaving(true);

      // Load the current content_json to merge our CLIN data into it
      let currentContentJson = proposalContentJson;
      if (!currentContentJson) {
        const { data: currentProposal } = await supabase
          .from('proposal_templates')
          .select('content_json')
          .eq('id', proposalId)
          .maybeSingle();
        currentContentJson = currentProposal?.content_json || {};
      }

      // Deep clone to avoid mutation issues
      const updatedContent = JSON.parse(JSON.stringify(currentContentJson));

      // Ensure pricingStrategy exists
      if (!updatedContent.pricingStrategy) {
        updatedContent.pricingStrategy = {};
      }

      // Save CLINs into pricingStrategy.clinStructure
      updatedContent.pricingStrategy.clinStructure = clins.map(c => ({
        id: c.id,
        clinNumber: c.clinNumber,
        title: c.title,
        description: c.description,
        contractType: c.type,
        type: c.type,
        periodOfPerformance: c.period,
        period: c.period,
        laborMix: c.laborMix,
        odcs: c.odcs,
        totalLabor: c.totalLabor,
        totalODC: c.totalODC,
        totalPrice: c.total,
        total: c.total
      }));

      updatedContent.pricingStrategy.wrapRate = wrapRate;
      updatedContent.pricingStrategy.clinUpdatedAt = new Date().toISOString();

      const { error } = await supabase
        .from('proposal_templates')
        .update({ content_json: updatedContent })
        .eq('id', proposalId);

      if (error) throw error;

      // Update local state so subsequent saves use the latest data
      setProposalContentJson(updatedContent);

      toast.success('CLIN structure saved successfully');
    } catch (error) {
      console.error('Error saving CLINs:', error);
      toast.error('Failed to save CLIN structure');
    } finally {
      setSaving(false);
    }
  };

  const getTotalContractValue = () => {
    return clins.reduce((sum, clin) => sum + clin.total, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-bw-primary" />
      </div>
    );
  }

  // Show proposal selector if no proposalId
  if (!proposalId) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <button
          onClick={() => navigate('/workspace/costing')}
          className="flex items-center text-sm text-bw-muted hover:text-bw-ink mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Costing Studio
        </button>
        <h1 className="text-2xl font-bold text-bw-ink flex items-center mb-2">
          <FileText className="h-6 w-6 mr-2 text-bw-primary" />
          CLIN Builder
        </h1>
        <p className="text-bw-muted mb-6">Select a proposal to build CLINs for:</p>

        {proposals.length > 0 ? (
          <div className="space-y-3">
            {proposals.map((proposal) => (
              <button
                key={proposal.id}
                onClick={() => setSelectedProposalId(proposal.id)}
                className="w-full text-left p-4 bg-bw-surface rounded-xl border border-bw-border hover:border-servet-blue hover:shadow-card transition-all"
              >
                <p className="font-medium text-bw-ink">{proposal.title}</p>
                <p className="text-sm text-bw-muted">{proposal.agency}</p>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-bw-surface rounded-xl border border-bw-border">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-medium text-bw-ink mb-2">No Proposals Found</h3>
            <p className="text-sm text-bw-muted">
              Generate a proposal first, then come back to build CLINs.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => navigate('/workspace/costing')}
            className="flex items-center text-sm text-bw-muted hover:text-bw-ink mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Costing Studio
          </button>
          <h1 className="text-2xl font-bold text-bw-ink flex items-center">
            <FileText className="h-6 w-6 mr-2 text-bw-primary" />
            CLIN Builder
          </h1>
          {contractTitle && (
            <p className="text-sm text-bw-muted mt-1">for {contractTitle}</p>
          )}
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={suggestCLINStructure}
            disabled={aiSuggesting || laborCategories.length === 0}
            className="flex items-center px-4 py-2 bg-bw-primary text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
          >
            {aiSuggesting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            AI Suggest Structure
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !proposalId}
            className="flex items-center px-4 py-2 bg-bw-primary text-white rounded-lg hover:bg-bw-primary transition-colors disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save CLINs
          </button>
        </div>
      </div>

      {/* Summary Bar */}
      <div className="bg-bw-primary rounded-xl p-4 text-white mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-blue-100">Total Contract Value</p>
            <p className="text-3xl font-bold">{formatCurrency(getTotalContractValue())}</p>
          </div>
          <div className="flex items-center space-x-6">
            <div className="text-center">
              <p className="text-2xl font-bold">{clins.length}</p>
              <p className="text-xs text-blue-100">CLINs</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{wrapRate.toFixed(2)}x</p>
              <p className="text-xs text-blue-100">Wrap Rate</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">
                {clins.reduce((sum, c) => sum + c.laborMix.reduce((s, l) => s + l.hours, 0), 0).toLocaleString()}
              </p>
              <p className="text-xs text-blue-100">Total Hours</p>
            </div>
          </div>
        </div>
      </div>

      {/* No Labor Categories Warning */}
      {laborCategories.length === 0 && (
        <div className="mb-6 p-4 bg-fit-moderate-bg border border-fit-moderate-border rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 text-fit-moderate mt-0.5 mr-3" />
          <div>
            <p className="font-medium text-fit-moderate">No Labor Categories Configured</p>
            <p className="text-sm text-fit-moderate mt-1">
              Configure your labor categories in the Pricing Wizard to build CLINs.
            </p>
            <button
              onClick={() => navigate('/workspace/costing/wizard')}
              className="mt-2 text-sm text-fit-moderate font-medium hover:text-yellow-900"
            >
              Go to Pricing Wizard →
            </button>
          </div>
        </div>
      )}

      {/* CLINs List */}
      <div className="space-y-4">
        {clins.map((clin) => (
          <div
            key={clin.id}
            className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden"
          >
            {/* CLIN Header */}
            <div
              className="flex items-center justify-between p-4 bg-bw-surface-subtle cursor-pointer hover:bg-bw-surface-subtle transition-colors"
              onClick={() => updateCLIN(clin.id, { isExpanded: !clin.isExpanded })}
            >
              <div className="flex items-center">
                {clin.isExpanded ? (
                  <ChevronDown className="h-5 w-5 text-bw-muted mr-2" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-bw-muted mr-2" />
                )}
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-mono font-medium text-bw-primary">
                      CLIN {clin.clinNumber}
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      clin.type === 'FFP' ? 'bg-success-bg text-success' :
                      clin.type === 'T&M' ? 'bg-bw-primary-tint text-bw-primary' :
                      'bg-bw-primary-tint text-bw-primary'
                    }`}>
                      {clin.type}
                    </span>
                    <span className="text-xs text-bw-muted">
                      {PERIODS.find(p => p.value === clin.period)?.label}
                    </span>
                  </div>
                  <p className="text-sm text-bw-muted mt-0.5">{clin.title || 'Untitled CLIN'}</p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <p className="text-lg font-semibold text-bw-ink">{formatCurrency(clin.total)}</p>
                  <p className="text-xs text-bw-muted">
                    {clin.laborMix.reduce((s, l) => s + l.hours, 0).toLocaleString()} hours
                  </p>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); duplicateCLIN(clin); }}
                    className="p-1.5 text-bw-muted hover:text-bw-primary transition-colors"
                    title="Duplicate CLIN"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeCLIN(clin.id); }}
                    className="p-1.5 text-bw-muted hover:text-urgent transition-colors"
                    title="Remove CLIN"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* CLIN Details */}
            {clin.isExpanded && (
              <div className="p-4 border-t border-bw-border space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-bw-muted mb-1">CLIN Number</label>
                    <input
                      type="text"
                      value={clin.clinNumber}
                      onChange={(e) => updateCLIN(clin.id, { clinNumber: e.target.value })}
                      className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm font-mono"
                      placeholder="0001"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-bw-muted mb-1">Title</label>
                    <input
                      type="text"
                      value={clin.title}
                      onChange={(e) => updateCLIN(clin.id, { title: e.target.value })}
                      className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm"
                      placeholder="CLIN Title"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-bw-muted mb-1">Contract Type</label>
                    <select
                      value={clin.type}
                      onChange={(e) => updateCLIN(clin.id, { type: e.target.value as CLINType })}
                      className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm"
                    >
                      {CLIN_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-bw-muted mb-1">Description</label>
                    <textarea
                      value={clin.description}
                      onChange={(e) => updateCLIN(clin.id, { description: e.target.value })}
                      className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm"
                      rows={2}
                      placeholder="Describe the scope of this CLIN..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-bw-muted mb-1">Period</label>
                    <select
                      value={clin.period}
                      onChange={(e) => updateCLIN(clin.id, { period: e.target.value as CLIN['period'] })}
                      className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm"
                    >
                      {PERIODS.map(period => (
                        <option key={period.value} value={period.value}>{period.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Labor Mix */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-bw-ink flex items-center">
                      <Users className="h-4 w-4 mr-1 text-bw-primary" />
                      Labor Mix
                    </label>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          addLaborToCLIN(clin.id, e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className="text-sm border border-bw-border rounded px-2 py-1"
                    >
                      <option value="">+ Add Labor Category</option>
                      {laborCategories
                        .filter(cat => !clin.laborMix.some(l => l.categoryId === cat.id))
                        .map(cat => (
                          <option key={cat.id} value={cat.id}>
                            {cat.title} (${cat.loadedRate.toFixed(2)}/hr)
                          </option>
                        ))}
                    </select>
                  </div>

                  {clin.laborMix.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-bw-border">
                          <th className="text-left py-2 text-bw-muted font-medium">Category</th>
                          <th className="text-right py-2 text-bw-muted font-medium">Rate</th>
                          <th className="text-right py-2 text-bw-muted font-medium w-32">Hours</th>
                          <th className="text-right py-2 text-bw-muted font-medium">Total</th>
                          <th className="w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {clin.laborMix.map((labor) => (
                          <tr key={labor.categoryId} className="border-b border-bw-border">
                            <td className="py-2">{labor.categoryTitle}</td>
                            <td className="py-2 text-right">{formatCurrency(labor.loadedRate)}/hr</td>
                            <td className="py-2 text-right">
                              <input
                                type="number"
                                value={labor.hours}
                                onChange={(e) => updateLaborMix(clin.id, labor.categoryId, parseInt(e.target.value) || 0)}
                                className="w-24 text-right border border-bw-border rounded px-2 py-1"
                              />
                            </td>
                            <td className="py-2 text-right font-medium">
                              {formatCurrency(labor.hours * labor.loadedRate)}
                            </td>
                            <td className="py-2">
                              <button
                                onClick={() => removeLaborFromCLIN(clin.id, labor.categoryId)}
                                className="text-bw-muted hover:text-urgent"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                        <tr className="font-medium">
                          <td colSpan={3} className="py-2 text-right">Labor Subtotal:</td>
                          <td className="py-2 text-right text-bw-primary">{formatCurrency(clin.totalLabor)}</td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-sm text-bw-muted text-center py-4">
                      No labor categories added. Select from the dropdown above.
                    </p>
                  )}
                </div>

                {/* ODCs */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-bw-ink flex items-center">
                      <Package className="h-4 w-4 mr-1 text-bw-primary" />
                      Other Direct Costs (ODCs)
                    </label>
                    <button
                      onClick={() => addODCToCLIN(clin.id)}
                      className="text-sm text-bw-primary hover:text-bw-primary"
                    >
                      + Add ODC
                    </button>
                  </div>

                  {clin.odcs.length > 0 ? (
                    <div className="space-y-2">
                      {clin.odcs.map((odc, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <input
                            type="text"
                            value={odc.name}
                            onChange={(e) => updateODC(clin.id, index, { name: e.target.value })}
                            placeholder="ODC Name"
                            className="flex-1 px-3 py-2 border border-bw-border rounded-lg text-sm"
                          />
                          <div className="flex items-center">
                            <DollarSign className="h-4 w-4 text-bw-muted" />
                            <input
                              type="number"
                              value={odc.amount}
                              onChange={(e) => updateODC(clin.id, index, { amount: parseFloat(e.target.value) || 0 })}
                              className="w-32 px-2 py-2 border border-bw-border rounded-lg text-sm text-right"
                            />
                          </div>
                          <button
                            onClick={() => removeODC(clin.id, index)}
                            className="text-bw-muted hover:text-urgent"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <div className="flex justify-end text-sm font-medium">
                        <span className="text-bw-muted mr-4">ODC Subtotal:</span>
                        <span className="text-bw-primary">{formatCurrency(clin.totalODC)}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-bw-muted text-center py-2">
                      No ODCs added
                    </p>
                  )}
                </div>

                {/* CLIN Total */}
                <div className="pt-4 border-t border-bw-border flex justify-end">
                  <div className="text-right">
                    <p className="text-sm text-bw-muted">CLIN Total</p>
                    <p className="text-2xl font-bold text-bw-ink">{formatCurrency(clin.total)}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Add CLIN Button */}
        <button
          onClick={addCLIN}
          className="w-full py-4 border-2 border-dashed border-bw-border rounded-xl text-bw-muted hover:border-servet-blue hover:text-bw-primary transition-colors flex items-center justify-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add CLIN
        </button>
      </div>

      {/* Empty State */}
      {clins.length === 0 && (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-medium text-bw-ink mb-2">No CLINs Yet</h3>
          <p className="text-sm text-bw-muted mb-4">
            Start by adding a CLIN or let AI suggest a structure based on your labor categories.
          </p>
          <div className="flex justify-center space-x-3">
            <button
              onClick={addCLIN}
              className="px-4 py-2 bg-bw-primary text-white rounded-lg hover:bg-bw-primary transition-colors"
            >
              <Plus className="h-4 w-4 mr-2 inline" />
              Add First CLIN
            </button>
            {laborCategories.length > 0 && (
              <button
                onClick={suggestCLINStructure}
                disabled={aiSuggesting}
                className="px-4 py-2 bg-bw-primary text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Sparkles className="h-4 w-4 mr-2 inline" />
                AI Suggest
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
